import { useState, useEffect } from "react";
import { Mail, Lock, User, Eye, EyeOff, Loader2, ArrowLeft, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { BSSLogo } from "./BSSLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LoginPage = ({ onLogin }) => {
  const [mode, setMode] = useState("login"); // login, register, forgot, reset
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);

  // Check URL for reset token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset');
    if (token) {
      setResetToken(token);
      setMode("reset");
      window.history.replaceState({}, '', '/');
    }
  }, []);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regRole, setRegRole] = useState("student"); // 'student' | 'founder'
  const [regCompany, setRegCompany] = useState("");
  const [invitationToken, setInvitationToken] = useState("");
  const [invitationInfo, setInvitationInfo] = useState(null); // { org_name, founder_name, email }

  // Detect invitation token from URL (/?invite=TOKEN or path /invite/TOKEN)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('invite');
    const pathMatch = window.location.pathname.match(/^\/invite\/([\w-]+)/);
    const token = fromQuery || (pathMatch ? pathMatch[1] : "");
    if (!token) return;
    setInvitationToken(token);
    setMode("register");
    axios.get(`${API}/invitations/${token}`)
      .then(res => {
        setInvitationInfo(res.data);
        setRegEmail(res.data.email || "");
        if (res.data.status !== 'pending') {
          toast.error("Jemputan ini sudah digunakan atau tamat tempoh");
        }
      })
      .catch(() => toast.error("Jemputan tidak dijumpai"));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPassword) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email: loginEmail.trim(), password: loginPassword });
      onLogin(res.data.token, res.data.user);
      toast.success(`Selamat datang, ${res.data.user.name}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login gagal");
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regName.trim() || !regEmail.trim() || !regPassword) { toast.error("Sila isi nama, email dan password"); return; }
    if (regPassword.length < 6) { toast.error("Password minimum 6 aksara"); return; }
    if (regRole === 'founder' && !regCompany.trim()) { toast.error("Founder mesti isi nama syarikat"); return; }
    setLoading(true);
    try {
      const payload = {
        email: regEmail.trim(), password: regPassword, name: regName.trim(),
        role: invitationToken ? 'staff' : regRole,
        company: regCompany.trim(),
      };
      if (invitationToken) payload.invitation_token = invitationToken;
      const res = await axios.post(`${API}/auth/register`, payload);
      onLogin(res.data.token, res.data.user);
      // Clean URL
      if (window.location.pathname.startsWith('/invite/')) {
        window.history.replaceState({}, '', '/');
      }
      toast.success(`Selamat datang, ${res.data.user.name}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pendaftaran gagal");
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error("Sila masukkan email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch (e) {
      toast.error("Gagal menghantar email reset");
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) { toast.error("Password minimum 6 aksara"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: newPassword });
      setResetDone(true);
      toast.success("Password berjaya ditukar!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal reset password");
    } finally { setLoading(false); }
  };

  const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
  );

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: '#FFFFFF' }} data-testid="login-page">
      {/* Subtle background pattern */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(37,99,235,0.04) 0%, transparent 50%)' }} />
      <div className="absolute top-20 right-20 w-72 h-72 rounded-full opacity-[0.03]" style={{ background: 'radial-gradient(circle, #2563EB 0%, transparent 70%)' }} />

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-[440px] mx-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4" style={{ filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.08))' }}>
            <BSSLogo size={88} testId="login-logo" />
          </div>
          <h1 className="text-3xl font-extrabold text-[#0F172A]" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.04em' }}>Selamat Datang</h1>
          <p className="text-sm mt-2 text-[#64748B]">Log masuk untuk teruskan pembelajaran anda</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-[20px] overflow-hidden" style={{ border: '1px solid #E2E8F0', boxShadow: '0 20px 60px rgba(0,0,0,0.06)' }}>
          {/* Tabs - only show in login/register mode */}
          {(mode === 'login' || mode === 'register') && (
          <div className="flex bg-[#F1F5F9] m-4 mb-0 rounded-full p-1">
            <button onClick={() => setMode("login")}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-full text-center transition-all duration-200 ${mode === 'login' ? 'bg-[#2563EB] text-white shadow-sm' : 'text-[#475569] hover:text-[#0F172A]'}`}
              data-testid="login-tab">Log Masuk</button>
            <button onClick={() => setMode("register")}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-full text-center transition-all duration-200 ${mode === 'register' ? 'bg-[#2563EB] text-white shadow-sm' : 'text-[#475569] hover:text-[#0F172A]'}`}
              data-testid="register-tab">Daftar Baru</button>
          </div>
          )}

          <div className="p-6 pt-5">
            {mode === "login" && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-[rgba(0,0,0,0.6)] mb-1.5 flex items-center gap-1.5"><Mail size={12} /> Email <span className="text-[#2563EB]">*</span></label>
                  <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="nama@email.com"
                    className="w-full px-4 py-3 rounded-[12px] border border-[rgba(0,0,0,0.08)] text-sm bg-[#F7F7F7] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all duration-200"
                    required autoFocus data-testid="login-email" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[rgba(0,0,0,0.6)] mb-1.5 flex items-center gap-1.5"><Lock size={12} /> Password <span className="text-[#2563EB]">*</span></label>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="Masukkan password"
                      className="w-full px-4 py-3 rounded-[12px] border border-[rgba(0,0,0,0.08)] text-sm bg-[#F7F7F7] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent pr-11 transition-all duration-200"
                      required data-testid="login-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Sembunyikan password' : 'Tunjuk password'} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full text-[rgba(0,0,0,0.55)] hover:text-[rgba(0,0,0,0.85)] hover:bg-black/5 transition-colors" data-testid="toggle-password-visibility">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <div className="text-right mt-1">
                    <button type="button" onClick={() => setMode("forgot")} className="text-[11px] text-[#2563EB] font-medium hover:underline" data-testid="forgot-password-link">Lupa password?</button>
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3.5 bg-[#2563EB] text-white rounded-full font-semibold text-sm disabled:opacity-50 transition-all duration-200 flex items-center justify-center gap-2 hover:bg-[#1D4ED8] hover:translate-y-[-1px] hover:shadow-lg active:scale-[0.97]"
                  data-testid="login-submit">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                  {loading ? 'Sedang log masuk...' : 'Log Masuk'}
                </button>

                <div className="relative py-1.5">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[rgba(0,0,0,0.06)]" /></div>
                  <div className="relative flex justify-center"><span className="bg-white px-4 text-xs text-[#64748B]">atau</span></div>
                </div>

                <button type="button" onClick={() => { window.location.href = `${API}/auth/google`; }}
                  className="w-full py-3 bg-white border border-[rgba(0,0,0,0.1)] rounded-full font-medium text-sm text-[#0A0A0A] flex items-center justify-center gap-2.5 hover:border-[rgba(0,0,0,0.2)] hover:shadow-sm hover:translate-y-[-1px] active:scale-[0.97] transition-all duration-200"
                  data-testid="google-login">
                  <GoogleIcon /> Log Masuk dengan Google
                </button>

                <p className="text-center text-xs text-[#475569] mt-3">
                  Belum ada akaun? <button type="button" onClick={() => setMode("register")} className="font-bold text-[#2563EB] hover:underline">Daftar sekarang</button>
                </p>
              </form>
            )}

            {mode === "register" && (
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Invitation banner */}
                {invitationInfo && (
                  <div className="p-3 rounded-xl bg-[#EFF6FF] border border-[#BFDBFE]" data-testid="invite-banner">
                    <p className="text-xs font-semibold text-[#1E3A8A] mb-0.5">
                      <strong>{invitationInfo.founder_name}</strong> menjemput anda
                    </p>
                    <p className="text-[11px] text-[#1E40AF]">
                      Daftar untuk menyertai pasukan <strong>{invitationInfo.org_name}</strong> sebagai staff
                    </p>
                  </div>
                )}

                {/* Role toggle (hidden if invitation) */}
                {!invitationToken && (
                  <div>
                    <label className="text-xs font-semibold text-[rgba(0,0,0,0.6)] mb-1.5 block">Saya daftar sebagai</label>
                    <div className="flex gap-2 p-1 bg-[#F7F7F7] rounded-full">
                      <button type="button" onClick={() => setRegRole('student')}
                        className={`flex-1 py-2 text-xs font-semibold rounded-full transition ${regRole === 'student' ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-[rgba(0,0,0,0.5)]'}`}
                        data-testid="role-student">Pelajar</button>
                      <button type="button" onClick={() => setRegRole('founder')}
                        className={`flex-1 py-2 text-xs font-semibold rounded-full transition ${regRole === 'founder' ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-[rgba(0,0,0,0.5)]'}`}
                        data-testid="role-founder">Founder (ada staff)</button>
                    </div>
                    {regRole === 'founder' && (
                      <p className="text-[10px] text-[rgba(0,0,0,0.45)] mt-1.5">Sebagai Founder, awak boleh jemput staff &amp; pantau progress mereka.</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="text-xs font-semibold text-[rgba(0,0,0,0.6)] mb-1.5 flex items-center gap-1.5"><User size={12} /> Nama Penuh <span className="text-[#2563EB]">*</span></label>
                  <input type="text" value={regName} onChange={(e) => setRegName(e.target.value)} placeholder="Nama penuh anda"
                    className="w-full px-4 py-3 rounded-[12px] border border-[rgba(0,0,0,0.08)] text-sm bg-[#F7F7F7] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all duration-200"
                    required autoFocus data-testid="reg-name" />
                </div>

                {regRole === 'founder' && !invitationToken && (
                  <div>
                    <label className="text-xs font-semibold text-[rgba(0,0,0,0.6)] mb-1.5 block">Nama Syarikat / Organisasi <span className="text-[#2563EB]">*</span></label>
                    <input type="text" value={regCompany} onChange={(e) => setRegCompany(e.target.value)} placeholder="cth: Acme Sdn Bhd"
                      className="w-full px-4 py-3 rounded-[12px] border border-[rgba(0,0,0,0.08)] text-sm bg-[#F7F7F7] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all duration-200"
                      required={regRole === 'founder'} data-testid="reg-company" />
                  </div>
                )}

                <div>
                  <label className="text-xs font-semibold text-[rgba(0,0,0,0.6)] mb-1.5 flex items-center gap-1.5"><Mail size={12} /> Email <span className="text-[#2563EB]">*</span></label>
                  <input type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} placeholder="nama@email.com"
                    readOnly={!!invitationToken}
                    className={`w-full px-4 py-3 rounded-[12px] border border-[rgba(0,0,0,0.08)] text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all duration-200 ${invitationToken ? 'bg-[#F2F2F2] text-[rgba(0,0,0,0.6)] cursor-not-allowed' : 'bg-[#F7F7F7] focus:bg-white'}`}
                    required data-testid="reg-email" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[rgba(0,0,0,0.6)] mb-1.5 flex items-center gap-1.5"><Lock size={12} /> Password <span className="text-[#2563EB]">*</span></label>
                  <input type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} placeholder="Minimum 6 aksara"
                    className="w-full px-4 py-3 rounded-[12px] border border-[rgba(0,0,0,0.08)] text-sm bg-[#F7F7F7] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all duration-200"
                    required data-testid="reg-password" />
                </div>

                <button type="submit" disabled={loading}
                  className="w-full py-3.5 bg-[#2563EB] text-white rounded-full font-semibold text-sm disabled:opacity-50 transition-all duration-200 flex items-center justify-center gap-2 hover:bg-[#1D4ED8] hover:translate-y-[-1px] hover:shadow-lg active:scale-[0.97]"
                  data-testid="register-submit">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                  {loading ? 'Sedang mendaftar...' : (invitationToken ? 'Terima Jemputan & Daftar' : 'Daftar & Mula Belajar')}
                </button>

                <div className="relative py-1">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[rgba(0,0,0,0.06)]" /></div>
                  <div className="relative flex justify-center"><span className="bg-white px-4 text-xs text-[#64748B]">atau</span></div>
                </div>

                <button type="button" onClick={() => { window.location.href = `${API}/auth/google${invitationToken ? `?invitation_token=${invitationToken}` : ''}`; }}
                  className="w-full py-2.5 bg-white border border-[rgba(0,0,0,0.1)] rounded-full font-medium text-sm text-[#0A0A0A] flex items-center justify-center gap-2.5 hover:border-[rgba(0,0,0,0.2)] hover:shadow-sm hover:translate-y-[-1px] active:scale-[0.97] transition-all duration-200"
                  data-testid="google-register">
                  <GoogleIcon /> Daftar dengan Google
                </button>

                <p className="text-center text-xs text-[rgba(0,0,0,0.35)]">
                  Sudah ada akaun? <button type="button" onClick={() => setMode("login")} className="font-bold text-[#2563EB] hover:underline">Log masuk</button>
                </p>
              </form>
            )}

            {/* Forgot Password */}
            {mode === "forgot" && (
              <div className="space-y-4">
                <button onClick={() => { setMode("login"); setForgotSent(false); }} className="flex items-center gap-1 text-xs text-[rgba(0,0,0,0.4)] hover:text-[rgba(0,0,0,0.6)] transition-colors">
                  <ArrowLeft size={14} /> Kembali ke Log Masuk
                </button>
                {forgotSent ? (
                  <div className="text-center py-4">
                    <div className="w-14 h-14 rounded-full bg-[#F7F7F7] flex items-center justify-center mx-auto mb-4"><CheckCircle size={28} className="text-emerald-500" /></div>
                    <h3 className="text-base font-bold text-[#0A0A0A] mb-1">Email Dihantar!</h3>
                    <p className="text-sm text-[rgba(0,0,0,0.4)]">Semak inbox anda untuk link reset password.</p>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="text-center mb-2">
                      <h3 className="text-base font-bold text-[#0A0A0A]">Lupa Password?</h3>
                      <p className="text-xs text-[rgba(0,0,0,0.4)] mt-1">Masukkan email anda, kami akan hantar link reset.</p>
                    </div>
                    <div>
                      <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="Email anda"
                        className="w-full px-4 py-3 rounded-[12px] border border-[rgba(0,0,0,0.08)] text-sm bg-[#F7F7F7] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all duration-200"
                        required autoFocus data-testid="forgot-email" />
                    </div>
                    <button type="submit" disabled={loading}
                      className="w-full py-3.5 bg-[#2563EB] text-white rounded-full font-semibold text-sm disabled:opacity-50 transition-all duration-200 flex items-center justify-center gap-2 hover:bg-[#1D4ED8] hover:translate-y-[-1px] hover:shadow-lg active:scale-[0.97]"
                      data-testid="forgot-submit">
                      {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                      {loading ? 'Menghantar...' : 'Hantar Link Reset'}
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* Reset Password */}
            {mode === "reset" && (
              <div className="space-y-4">
                {resetDone ? (
                  <div className="text-center py-4">
                    <div className="w-14 h-14 rounded-full bg-[#F7F7F7] flex items-center justify-center mx-auto mb-4"><CheckCircle size={28} className="text-emerald-500" /></div>
                    <h3 className="text-base font-bold text-[#0A0A0A] mb-1">Password Ditukar!</h3>
                    <p className="text-sm text-[rgba(0,0,0,0.4)] mb-4">Sila log masuk dengan password baru anda.</p>
                    <button onClick={() => { setMode("login"); setResetDone(false); }}
                      className="px-6 py-2.5 bg-[#2563EB] text-white rounded-full font-semibold text-sm hover:bg-[#1D4ED8] hover:translate-y-[-1px] hover:shadow-lg active:scale-[0.97] transition-all duration-200">
                      Log Masuk
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div className="text-center mb-2">
                      <h3 className="text-base font-bold text-[#0A0A0A]">Reset Password</h3>
                      <p className="text-xs text-[rgba(0,0,0,0.4)] mt-1">Masukkan password baru anda.</p>
                    </div>
                    <div>
                      <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Password baru (min 6 aksara)"
                        className="w-full px-4 py-3 rounded-[12px] border border-[rgba(0,0,0,0.08)] text-sm bg-[#F7F7F7] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all duration-200"
                        required autoFocus data-testid="reset-password" />
                    </div>
                    <button type="submit" disabled={loading}
                      className="w-full py-3.5 bg-[#2563EB] text-white rounded-full font-semibold text-sm disabled:opacity-50 transition-all duration-200 flex items-center justify-center gap-2 hover:bg-[#1D4ED8] hover:translate-y-[-1px] hover:shadow-lg active:scale-[0.97]"
                      data-testid="reset-submit">
                      {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                      {loading ? 'Menukar...' : 'Tukar Password'}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-[10px] mt-6 text-[rgba(0,0,0,0.2)]">BSS Portal &copy; 2026. Business Sustainability Solution</p>
      </div>
    </div>
  );
};
